import { DodStateName } from '../components/stateChip/StateChip.types';
import { dodReaderLabel } from '../overviewTab/dodFormat';

/**
 * Pure helpers for the Dead letter tab.
 *
 * Wire shapes are described structurally rather than imported from `api/dod/dod.v2.types`, so a
 * rename or a reshape on the API side lands in one mapper instead of in every presenter and test.
 */

export type DlqWireEntry = {
  id: string;
  rcpId: string;
  nlk: number;
  numerRozkazu: number;
  rozkaz: string;
  kadId?: string;
  enqueuedAt: number;
  retryCount: number;
  maxRetries: number;
  lastError?: string;
  lastAttemptAt?: number;
  rcpNazwa?: string;
  tkdNazwa?: string;
  reason: DlqReason;
  errorClass: DlqErrorClass;
  errorCode?: number;
  errorName?: string;
};

export type DlqReason = 'permanent' | 'max-retries' | 'unknown-cap';

export type DlqErrorClass = 'PERMANENT' | 'TRANSIENT' | 'OPERATION' | 'UNKNOWN';

export const DLQ_REASONS: DlqReason[] = ['permanent', 'max-retries', 'unknown-cap'];

export const DLQ_ERROR_CLASSES: DlqErrorClass[] = ['PERMANENT', 'TRANSIENT', 'OPERATION', 'UNKNOWN'];

export const DLQ_PAGE_SIZES = [50, 100, 200];

/** Page size used while walking the whole filter for an export. The backend's maximum. */
export const DLQ_EXPORT_PAGE_SIZE = 200;

/** Hard cap on one CSV export, mirroring DOD_HISTORY_CSV_MAX_ROWS on the backend. */
export const DLQ_CSV_MAX_ROWS = 10_000;

export type DlqRow = {
  id: string;
  rcpId: string;
  rcpLabel: string;
  nlk: number;
  tkdLabel: string;
  numerRozkazu: number;
  /** The card as the order carries it, in full. */
  kad: string;
  retryCount: number;
  maxRetries: number;
  reason: DlqReason;
  errorClass: DlqErrorClass;
  errorCode?: number;
  errorName?: string;
  lastError: string;
  /** lastAttemptAt when the order was tried, else the moment it was enqueued. */
  timestamp: number;
  /** The untouched wire entry, shown verbatim in the expanded JSON row. */
  raw: DlqWireEntry;
};

/** The time column: the last attempt if there was one, otherwise when the order was enqueued. */
export const dlqEntryTimestamp = (entry: Pick<DlqWireEntry, 'lastAttemptAt' | 'enqueuedAt'>): number =>
  entry.lastAttemptAt ?? entry.enqueuedAt;

export const toDlqRow = (entry: DlqWireEntry): DlqRow => ({
  id: entry.id,
  rcpId: entry.rcpId,
  rcpLabel: entry.rcpNazwa ?? entry.rcpId,
  nlk: entry.nlk,
  tkdLabel: dodReaderLabel(entry.tkdNazwa, entry.nlk),
  numerRozkazu: entry.numerRozkazu,
  kad: entry.kadId ? String(entry.kadId) : '—',
  retryCount: entry.retryCount,
  maxRetries: entry.maxRetries,
  reason: entry.reason,
  errorClass: entry.errorClass,
  errorCode: entry.errorCode,
  errorName: entry.errorName,
  lastError: entry.lastError ?? '',
  timestamp: dlqEntryTimestamp(entry),
  raw: entry,
});

/** Reason and error class each have their own StateChip words; an unknown class falls back to neutral. */
export const dlqReasonState = (reason: DlqReason): DodStateName => reason;

export const dlqErrorClassState = (errorClass: DlqErrorClass): DodStateName => {
  switch (errorClass) {
    case 'PERMANENT':
      return 'permanent';
    case 'TRANSIENT':
      return 'transient';
    case 'OPERATION':
      return 'operation';
    default:
      return 'unknown';
  }
};

/** "-58 ParseFail" when the backend parsed a code, otherwise the raw error text. */
export const dlqErrorLabel = (row: Pick<DlqRow, 'errorCode' | 'errorName' | 'lastError'>): string => {
  if (row.errorCode === undefined) {
    return row.lastError;
  }

  return row.errorName ? `${row.errorCode} ${row.errorName}` : String(row.errorCode);
};

/**
 * What the details panel prints as the order's payload.
 *
 * The raw RCP frame when the backend recorded one, because that is what an operator compares with
 * a protocol capture; otherwise the untouched wire entry, so nothing the backend sent is hidden.
 * Both verbatim.
 */
export const dlqPayloadText = (row: Pick<DlqRow, 'raw'>): string =>
  row.raw.rozkaz ? row.raw.rozkaz : JSON.stringify(row.raw, null, 2);

export const formatClockTime = (timestamp: number): string => {
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  const pad = (value: number): string => String(value).padStart(2, '0');

  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const CSV_HEADER = [
  'timestamp',
  'rcpId',
  'rcpNazwa',
  'nlk',
  'tkdNazwa',
  'numerRozkazu',
  'rozkaz',
  'kad',
  'retryCount',
  'maxRetries',
  'reason',
  'errorClass',
  'errorCode',
  'errorName',
  'lastError',
];

/**
 * RFC 4180 quoting: every field is quoted and embedded quotes are doubled, so an error text holding
 * a comma, a quote or a newline cannot shift the remaining columns of that row.
 */
export const csvCell = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '""';
  }

  return `"${String(value).split('"').join('""')}"`;
};

const isoOrEmpty = (timestamp: number): string => {
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

/** Builds the dead-letter CSV: every value verbatim, as the table shows it. */
export const buildDlqCsv = (rows: DlqRow[]): string =>
  [CSV_HEADER.map(csvCell).join(',')]
    .concat(
      rows
        .slice(0, DLQ_CSV_MAX_ROWS)
        .map((row) =>
          [
            isoOrEmpty(row.timestamp),
            row.rcpId,
            row.rcpLabel,
            row.nlk,
            row.tkdLabel,
            row.numerRozkazu,
            row.raw.rozkaz ?? '',
            row.kad,
            row.retryCount,
            row.maxRetries,
            row.reason,
            row.errorClass,
            row.errorCode ?? '',
            row.errorName ?? '',
            row.lastError,
          ]
            .map(csvCell)
            .join(','),
        ),
    )
    .join('\n');

export type DlqQueryFilter = {
  rcpId?: string;
  nlk?: number;
  command?: number;
  reason?: DlqReason;
  errorClass?: DlqErrorClass;
  from?: number;
  to?: number;
  search?: string;
};

export type DlqFilterInput = {
  /** Cross-tab filters from `useDodFilters()`; every value is a raw URL string ('' = unset). */
  shared: { rcp: string; nlk: string; cmd: string };
  fromTs: number;
  toTs: number;
  reason: DlqReason | '';
  errorClass: DlqErrorClass | '';
  search: string;
};

const numberOrUndefined = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * Folds the URL-backed filters and the two tab-local selects into the query the backend expects.
 *
 * The same object is sent as `filter` in a "retry by filter" bulk request, which is why it is built
 * once here: the rows a user sees and the rows a bulk action touches must come from one definition.
 */
export const toDlqQueryFilter = (input: DlqFilterInput): DlqQueryFilter => {
  const filter: DlqQueryFilter = {
    from: input.fromTs,
    to: input.toTs,
  };

  if (input.shared.rcp) {
    filter.rcpId = input.shared.rcp;
  }

  const nlk = numberOrUndefined(input.shared.nlk);
  if (nlk !== undefined) {
    filter.nlk = nlk;
  }

  const command = numberOrUndefined(input.shared.cmd);
  if (command !== undefined) {
    filter.command = command;
  }

  if (input.reason) {
    filter.reason = input.reason;
  }

  if (input.errorClass) {
    filter.errorClass = input.errorClass;
  }

  if (input.search.trim()) {
    filter.search = input.search.trim();
  }

  return filter;
};

/**
 * Pages the caller's fetcher walks to collect every dead-letter row matching a filter.
 *
 * Stops on the first short page, on an empty page and at `DLQ_CSV_MAX_ROWS`, so a backend that
 * ignores `page` and keeps returning the first one cannot spin this forever. The result is
 * truncated to the cap exactly, and `truncated` says whether anything was left behind.
 */
export const collectDlqPages = async (
  fetchPage: (page: number, pageSize: number) => Promise<{ items: DlqWireEntry[]; total: number }>,
  maxRows = DLQ_CSV_MAX_ROWS,
): Promise<{ rows: DlqRow[]; truncated: boolean }> => {
  const collected: DlqRow[] = [];
  const seen = new Set<string>();
  let page = 1;
  let total = 0;

  while (collected.length < maxRows) {
    const response = await fetchPage(page, DLQ_EXPORT_PAGE_SIZE);
    const items = response?.items ?? [];

    total = response?.total ?? total;

    if (items.length === 0) {
      break;
    }

    const fresh = items.filter((item) => !seen.has(item.id));

    if (fresh.length === 0) {
      break;
    }

    fresh.forEach((item) => {
      seen.add(item.id);
      collected.push(toDlqRow(item));
    });

    if (items.length < DLQ_EXPORT_PAGE_SIZE) {
      break;
    }

    page += 1;
  }

  return {
    rows: collected.slice(0, maxRows),
    truncated: collected.length > maxRows || (total > maxRows && collected.length >= maxRows),
  };
};
